import { IOperative } from '../message-models/operative.interface';
import { DegreeOfSupervisionString, RiskMatrixLevel } from '../message-models/risk-matrix-level.enum';
import { IRiskInfo } from '../message-models/risk-info.interface';
import { IJobLevelInfo } from '../message-models/job-level.interface';
import { IRiskMatrixEntry } from '../message-models/risk-matrix-entry.interface';
import { IDateTimeWithZone } from '../common/date-time-with-zone.interface';
import { consts } from '../common/modals/consts';
import { DateTimeWithZone } from '../common/date-time-with-zone.class';
import { IEmploymentStatus } from '../message-models/employment-status.interface';
import { ITrainingRecord } from '../message-models/training-record.interface';

export class OperativeInfo implements IOperative {
  id: string;
  currentJobLevelId: string;
  currentRiskId: string;
  currentEmploymentStatusId: string;

  fullName: string;
  lastObserved: string;
  numTrainingRecords: number;
  referenceNumbers: string;
  nextFollowUp: string;
  firstName: string;
  middleInitials: string;
  lastName: string;
  currentRisk: string;
  currentJobLevel: string;
  riskMatrixLevel: RiskMatrixLevel;
  jobTitle: string;
  emailAddress: string;
  phoneNumber: string;
  currentJobTitleId: string;
  companyId: string;
  latestEngagementFrom: IDateTimeWithZone;
  latestEngagementTo: IDateTimeWithZone;
  followUpOn: IDateTimeWithZone;
  latestEngagementId: string;
  image: string;
  currentOperativeTypeId: string;
  operativeType: string;
  currentBusinessUnitId: string;
  businessUnit: string;

  jobLevel?: any;
  currentWorkRisk?: any;
  currentRiskMatrixLevel?: any;
  currentEmploymentStatus?: any;

  totalCPDHours: number;
  trainingRecords: ITrainingRecord[];

  constructor(operative: IOperative = null) {
    this.id = operative?.id || consts.emptyGuid;
    this.firstName = operative?.firstName || '';
    this.middleInitials = operative?.middleInitials || '';
    this.lastName = operative?.lastName || '';
    this.currentJobLevelId = operative?.currentJobLevelId || '';
    this.currentRiskId = operative?.currentRiskId || '';
    this.currentJobTitleId = operative?.currentJobTitleId || '';
    this.latestEngagementFrom = new DateTimeWithZone(operative?.latestEngagementFrom || new DateTimeWithZone({
      dateTime: new Date(new Date().setHours(1, 0, 0, 0)).toISOString(),
      timeZone: ''
    }));
    if (!this.latestEngagementFrom.dateTime) {
      this.latestEngagementFrom.dateTime = new Date(new Date().setHours(1, 0, 0, 0)).toISOString();
    }

    this.latestEngagementTo = operative?.latestEngagementTo || { dateTime: null } as IDateTimeWithZone;
    this.referenceNumbers = operative?.referenceNumbers || '';
    this.lastObserved = operative?.lastObserved || '';
    this.numTrainingRecords = operative?.numTrainingRecords || 0;
    this.nextFollowUp = operative?.nextFollowUp || '';
    this.jobTitle = operative?.jobTitle || '';
    this.emailAddress = operative?.emailAddress || '';
    this.phoneNumber = operative?.phoneNumber || '';
    this.companyId = operative?.companyId || '';
    this.latestEngagementId = operative?.latestEngagementId || consts.emptyGuid;
    this.fullName = operative?.lastName + ', ' + operative?.middleInitials + ' ' + operative?.firstName || '';
    this.followUpOn = operative?.followUpOn || { dateTime: null } as IDateTimeWithZone;
    this.currentEmploymentStatusId = operative?.currentEmploymentStatusId || consts.emptyGuid;
    this.image = operative?.image || "";
    this.currentOperativeTypeId = operative?.currentOperativeTypeId || '';
    this.operativeType = operative?.operativeType || '';
    this.currentBusinessUnitId = operative?.currentBusinessUnitId || '';
    this.businessUnit = operative?.businessUnit || '';
    this.trainingRecords = operative?.trainingRecords;
  }

  populateRisk(risks: IRiskInfo[]) {
    if (!this.currentRiskId) {
      this.currentRisk = '';
      return;
    }
    const currentRisk = risks.filter(r => r.id === this.currentRiskId);
    if (currentRisk && currentRisk.length > 0) {
      this.currentRisk = currentRisk[0].name;
      this.currentWorkRisk = { label: currentRisk[0].name, value: currentRisk[0].id };
    } else {
      this.currentRisk = '';
      this.currentWorkRisk = null;
    }
  }

  getFullName() {
    this.fullName = this.firstName + ' ' + this.middleInitials + ' ' + this.lastName;
    return this.fullName;
  }

  populateJobLevel(jobLevels: IJobLevelInfo[]) {
    if (!this.currentJobLevelId) {
      this.currentJobLevel = '';
      return;
    }

    const currentJobLevel = jobLevels.filter(l => l.id === this.currentJobLevelId);
    if (currentJobLevel && currentJobLevel.length > 0) {
      this.currentJobLevel = currentJobLevel[0].name;
      this.jobLevel = { label: currentJobLevel[0].name, value: currentJobLevel[0].id };
    } else {
      this.currentJobLevel = '';
      this.jobLevel = null;
    }
  }

  setDefaultEmploymentStatus(employmentStatuses: IEmploymentStatus[]) {
    const defaultEmployment = employmentStatuses.find(c => c.isDefault === true);
    if (defaultEmployment) {
      this.currentEmploymentStatusId = defaultEmployment.id;
    }
  }

  populateEmploymentStatus(employmentStatuses: IEmploymentStatus[]) {
    const defaultEmploymentStatus = employmentStatuses.find(l => l.isDefault);

    if (!this.currentEmploymentStatusId) {
      this.currentEmploymentStatus = { label: defaultEmploymentStatus?.name, value: defaultEmploymentStatus?.id };
      this.currentEmploymentStatusId = defaultEmploymentStatus?.id;
      return;
    }

    const currentEmploymentStatus = employmentStatuses.find(l => l.id === this.currentEmploymentStatusId);
    if (currentEmploymentStatus) {
      this.currentEmploymentStatus = { label: currentEmploymentStatus?.name, value: currentEmploymentStatus?.id };
    } else {
      this.currentEmploymentStatus = { label: defaultEmploymentStatus?.name, value: defaultEmploymentStatus?.id };
      this.currentEmploymentStatusId = defaultEmploymentStatus?.id;
    }
  }

  populateRiskMatrixLevel(riskMatrix: IRiskMatrixEntry[]) {
    if (!riskMatrix) {
      return;
    }

    this.riskMatrixLevel = null;
    riskMatrix.forEach(riskEntry => {
      if (riskEntry.jobLevelId === this.currentJobLevelId && riskEntry.riskId === this.currentRiskId) {
        this.riskMatrixLevel = riskEntry.riskMatrixLevel;
        this.currentRiskMatrixLevel = { label: this.jobTitle === 'Qualified Supervisor' ? 'QS' : DegreeOfSupervisionString[this.riskMatrixLevel], value: this.jobTitle === 'Qualified Supervisor' ? 'QS' : this.riskMatrixLevel };
        return;
      }
    });

    if (this.riskMatrixLevel === null) {
      this.riskMatrixLevel = RiskMatrixLevel.danger;
      this.currentRiskMatrixLevel = { label: DegreeOfSupervisionString.High, value: this.riskMatrixLevel };
    }

  }

  getRiskColour(): string {
    switch (this.riskMatrixLevel) {
      case RiskMatrixLevel.low:
        return 'darkgreen';
      case RiskMatrixLevel.warning:
        return 'darkorange';
      case RiskMatrixLevel.danger:
        return 'red';
    }
    return 'black';
  }

  getRiskClass(): string {
    switch (this.riskMatrixLevel) {
      case RiskMatrixLevel.low:
        return 'low-risk';
      case RiskMatrixLevel.warning:
        return 'medium-risk';
      case RiskMatrixLevel.danger:
        return 'high-risk';
    }
    return '';
  }

  getRiskName(): string {
    switch (this.riskMatrixLevel) {
      case RiskMatrixLevel.low:
        return '1 - Low';
      case RiskMatrixLevel.warning:
        return '2 - Warning';
      case RiskMatrixLevel.danger:
        return '3 - Danger';
    }
    return this.riskMatrixLevel;
  }

  getDegreeOfRisk(): string {
    switch (this.riskMatrixLevel) {
      case RiskMatrixLevel.low:
        return 'Low Degree of Risk';
      case RiskMatrixLevel.warning:
        return 'Medium Degree of Risk';
      case RiskMatrixLevel.danger:
        return 'High Degree of Risk';
    }
    return this.riskMatrixLevel;
  }

  getSupervisionRequirement(): string {
    if (this.currentOperativeTypeId == "9e693556-8c16-44a2-b1e4-2c8d18b081ad") {
      switch (this.riskMatrixLevel) {
        case RiskMatrixLevel.low:
          return 'Risk - Verbal Instruction / Remote QS Involvement on Site';
        case RiskMatrixLevel.warning:
          return 'Risk - Written Instruction / Periodic QS Involvement on Site';
        case RiskMatrixLevel.danger:
          return 'Risk - Written Instruction / Close and Detailed QS Involvement on Site';
      }
    }
    else {
      switch (this.riskMatrixLevel) {
        case RiskMatrixLevel.low:
          return 'EAS - Verbal Instruction / Remote QS Involvement on Site';
        case RiskMatrixLevel.warning:
          return 'EAS - Written Instruction / Periodic QS Involvement on Site';
        case RiskMatrixLevel.danger:
          return 'EAS - Written Instruction / Close and Detailed QS Involvement on Site';
      }
    }
    
    return '';
  }


  getRiskBadgeClass() {
    switch (this.riskMatrixLevel) {
      case RiskMatrixLevel.low:
        return 'badge-low-risk';
      case RiskMatrixLevel.warning:
        return 'badge-medium-risk';
      case RiskMatrixLevel.danger:
        return 'badge-high-risk';
    }
    return '';
  }

  get startedOn(): string {
    return this.latestEngagementFrom?.dateTime;
  }

  get endedOn(): string {
    return this.latestEngagementTo?.dateTime;
  }

  get nextObservationFollowUp(): string {
    return this.followUpOn?.dateTime;
  }

  get isValid(): boolean {
    return !!this.lastName && !!this.firstName && !!this.currentJobTitleId && !!this.currentRiskId && !!this.currentJobLevelId && !!this.startedOn && !!this.currentEmploymentStatusId && !!this.currentOperativeTypeId;
  }
}
